import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HOUR_LABELS,
  WEEK_DAYS,
} from "../ShopifyGeoSpatialDashboard.data";
import { useShopifySalesAnalytics } from "../../../hooks/UseShopifySalesAnalytics";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { DashboardExportButton, DashboardNarrativePanel } from "src/components/common";
import DashboardSectionHeader from "src/components/common/DashboardSectionHeader";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import { normalizeApiLang } from "src/utils/apiLang";
import OrganicDateRangePicker from "src/components/common/OrganicDateRangePicker/OrganicDateRangePicker";
import { CHART_PRIMARY_COLOR } from "src/components/charts/ChartPalette";
import ShopifyGeoActiveFilters from "./ShopifyGeoActiveFilters";
import ShopifyGeoRegionCard from "./ShopifyGeoRegionCard";
import {
  type GeoCrossFilter,
  formatGeoFilterChipText,
  hasCrossFilterParams,
  inferPakistanRegionSlug,
  normalizeCity,
  REGION_DISPLAY_ORDER,
  regionLabelForChip,
  resolveCityDisplayName,
  stableColorForProductName,
  transformApiRegionToShopifyRegion,
} from "./ShopifyGeoSpatialDashboard.utils";
import "../../../styles/ShopifyGeoSpatialDashboard.scss";

interface ShopifyGeoSpatialDashboardProps {
  showHeader?: boolean;
}

type GeoLayoutWidth = "wide" | "compact" | "stacked";

const ShopifyGeoSpatialDashboard: FC<ShopifyGeoSpatialDashboardProps> = ({
  showHeader = true,
}) => {
  const { t, i18n } = useTranslation();
  const apiLang = normalizeApiLang(i18n.language);
  const todayISO = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [crossFilter, setCrossFilter] = useState<GeoCrossFilter>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [layoutWidth, setLayoutWidth] = useState<GeoLayoutWidth>("wide");

  /** Remember each product's color from full top-5 responses; filtered responses often repeat the first-slot color. */
  const productDonutColorCacheRef = useRef(new Map<string, string>());

  const apiCrossFilter = useMemo(() => crossFilter, [crossFilter]);

  const { data, loading, error } = useShopifySalesAnalytics(
    startDate,
    endDate,
    apiLang,
    apiCrossFilter,
  );

  const weekDays = useMemo(() => {
    const v = t("shopifyGeo.weekDays", { returnObjects: true });
    return Array.isArray(v) && v.length === 7 ? (v as string[]) : WEEK_DAYS;
  }, [t]);

  const hourLabels = useMemo(() => {
    const v = t("shopifyGeo.hourLabels", { returnObjects: true });
    return Array.isArray(v) && v.length === 24 ? (v as string[]) : HOUR_LABELS;
  }, [t]);

  const regions = useMemo(() => {
    if (!data?.regions?.length) {
      return [];
    }
    const cache = productDonutColorCacheRef.current;
    return data.regions
      .map(panel => {
        const slug = inferPakistanRegionSlug(panel.id, panel.title);
        const list = panel.top_products ?? [];

        if (list.length >= 2) {
          for (const p of list) {
            if (p.name) {
              cache.set(`${slug}::${p.name}`, stableColorForProductName(p.name));
            }
          }
        }

        const region = transformApiRegionToShopifyRegion(panel, t);
        const products = region.products.map(pr => {
          const key = `${slug}::${pr.name}`;
          const cached = cache.get(key);
          const color =
            cached ??
            stableColorForProductName(pr.name) ??
            CHART_PRIMARY_COLOR;
          return { ...pr, color };
        });

        return { ...region, products };
      })
      .sort((a, b) => {
        const aSlug = inferPakistanRegionSlug(a.id, a.title);
        const bSlug = inferPakistanRegionSlug(b.id, b.title);
        const aOrder = REGION_DISPLAY_ORDER[aSlug] ?? 99;
        const bOrder = REGION_DISPLAY_ORDER[bSlug] ?? 99;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return a.title.localeCompare(b.title);
      });
  }, [data, t]);

  const activeFilterState = useMemo(() => {
    if (crossFilter !== null) {
      return {
        region: crossFilter.filter_region?.trim() || null,
        citySlug: crossFilter.filter_city?.trim() || null,
        hour: typeof crossFilter.filter_hour === "number" ? crossFilter.filter_hour : null,
        weekday: typeof crossFilter.filter_weekday === "number" ? crossFilter.filter_weekday : null,
      };
    }
    return {
      region: data?.active_filter?.region?.trim() || null,
      citySlug: data?.active_filter?.city?.trim() || null,
      hour: data?.active_filter?.hour ?? null,
      weekday: data?.active_filter?.weekday ?? null,
    };
  }, [crossFilter, data?.active_filter]);

  const activeRegionSlug = useMemo(
    () => activeFilterState.region?.trim().toLowerCase() ?? null,
    [activeFilterState.region],
  );

  const focusedRegion = useMemo(() => {
    if (!activeRegionSlug) {
      return null;
    }
    return (
      regions.find(
        region => inferPakistanRegionSlug(region.id, region.title) === activeRegionSlug,
      ) ?? null
    );
  }, [regions, activeRegionSlug]);

  const resolvedCityDisplayName = useMemo(
    () => resolveCityDisplayName(activeFilterState.citySlug, focusedRegion?.cities),
    [activeFilterState.citySlug, focusedRegion?.cities],
  );

  const heatmapFilterLabel = useMemo(() => {
    const { hour, weekday } = activeFilterState;
    if (typeof hour !== "number" || typeof weekday !== "number") {
      return null;
    }
    const day = weekDays[weekday] ?? "";
    const hr = hourLabels[hour] ?? "";
    if (!day || !hr) {
      return null;
    }
    return `${day} · ${hr}`;
  }, [activeFilterState, weekDays, hourLabels]);

  const filterRegionLabel = useMemo(() => {
    if (!activeRegionSlug) {
      return "";
    }
    return regionLabelForChip(activeRegionSlug, regions).trim();
  }, [activeRegionSlug, regions]);

  const filterChipText = useMemo(() => {
    const filterInput = {
      region: activeFilterState.region,
      city: activeFilterState.citySlug,
      hour: activeFilterState.hour,
      weekday: activeFilterState.weekday,
    };
    if (!filterInput.region) {
      return "";
    }
    return formatGeoFilterChipText(
      filterInput,
      t,
      weekDays,
      hourLabels,
      regions,
      resolvedCityDisplayName,
    );
  }, [
    activeFilterState,
    t,
    weekDays,
    hourLabels,
    regions,
    resolvedCityDisplayName,
  ]);

  const removeRegionFilter = useCallback(() => setCrossFilter(null), []);
  const removeCityFilter = useCallback(() => {
    const region = crossFilter?.filter_region ?? activeFilterState.region;
    if (!region) {return;}
    const hourVal = crossFilter?.filter_hour ?? activeFilterState.hour;
    const weekdayVal = crossFilter?.filter_weekday ?? activeFilterState.weekday;
    setCrossFilter({
      filter_region: region,
      filter_hour: typeof hourVal === "number" ? hourVal : undefined,
      filter_weekday: typeof weekdayVal === "number" ? weekdayVal : undefined,
    });
  }, [crossFilter, activeFilterState]);
  const removeTimeFilter = useCallback(() => {
    const region = crossFilter?.filter_region ?? activeFilterState.region;
    if (!region) {return;}
    const cityVal = crossFilter?.filter_city ?? activeFilterState.citySlug;
    setCrossFilter({
      filter_region: region,
      filter_city: cityVal || undefined,
    });
  }, [crossFilter, activeFilterState]);

  const mapSelectedCityNormalized = useMemo(() => {
    if (crossFilter?.filter_city) {
      return normalizeCity(crossFilter.filter_city);
    }
    const c = data?.active_filter?.city;
    return c ? normalizeCity(c) : null;
  }, [crossFilter, data?.active_filter?.city]);

  const mapSelectedRegionId = useMemo(() => activeRegionSlug, [activeRegionSlug]);

  const visibleRegions = useMemo(() => {
    if (!activeRegionSlug) {
      return regions;
    }
    const filtered = regions.filter(
      region => inferPakistanRegionSlug(region.id, region.title) === activeRegionSlug,
    );
    return filtered.length ? filtered : regions;
  }, [regions, activeRegionSlug]);

  const isFocusedFilterView = visibleRegions.length === 1;

  const narrativePayloadData = useMemo(
    () => data && typeof data === "object" && !Array.isArray(data)
      ? data as unknown as Record<string, unknown>
      : {},
    [data],
  );
  const { narrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "geo_spatial",
    tabKey: "overview",
    startDate,
    endDate,
    data: narrativePayloadData,
    enabled: !loading && !error && Boolean(data),
  });
  const narrativePanelLoading = loading || narrativeLoading || (!data && !error);

  const showFilterBar = Boolean(
    hasCrossFilterParams(crossFilter) || data?.active_filter?.region,
  );

  useEffect(() => {
    const node = mainRef.current;
    if (!node) {
      return;
    }

    const syncWidth = (width: number) => {
      if (width < 560) {
        setLayoutWidth("stacked");
      } else if (width < 960) {
        setLayoutWidth("compact");
      } else {
        setLayoutWidth("wide");
      }
    };

    syncWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        syncWidth(entry.contentRect.width);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, showFilterBar, visibleRegions.length, isFocusedFilterView]);

  const formatDisplayDate = useCallback((dateInput: string | Date) => {
    const parsed = typeof dateInput === "string"
      ? new Date(`${dateInput}T00:00:00`)
      : dateInput;
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    return `${day}-${month}-${parsed.getFullYear()}`;
  }, []);

  const dateRangeLabel = useMemo(() => {
    if (data?.date_range_label) {
      return data.date_range_label;
    }
    const { currentFrom, currentTo, previousFrom, previousTo } = (() => {
      if (data?.current_period && data?.comparison_period) {
        return {
          currentFrom: formatDisplayDate(data.current_period.start_date),
          currentTo: formatDisplayDate(data.current_period.end_date),
          previousFrom: formatDisplayDate(data.comparison_period.start_date),
          previousTo: formatDisplayDate(data.comparison_period.end_date),
        };
      }
      const startObj = new Date(`${startDate}T00:00:00`);
      const endObj = new Date(`${endDate}T00:00:00`);
      const periodDays = Math.max(1, Math.floor((endObj.getTime() - startObj.getTime()) / (24 * 60 * 60 * 1000)) + 1);
      const prevEnd = new Date(startObj);
      prevEnd.setDate(prevEnd.getDate() - 1);
      const prevStart = new Date(prevEnd);
      prevStart.setDate(prevStart.getDate() - (periodDays - 1));
      return {
        currentFrom: formatDisplayDate(startDate),
        currentTo: formatDisplayDate(endDate),
        previousFrom: formatDisplayDate(prevStart),
        previousTo: formatDisplayDate(prevEnd),
      };
    })();
    return t("shopifyGeo.dateRange.showing", {
      currentFrom,
      currentTo,
      previousFrom,
      previousTo,
    });
  }, [data, startDate, endDate, t, formatDisplayDate]);

  return (
    <div
      ref={pdfContainerRef}
      className={[
        "shopify-geo",
        showFilterBar ? "shopify-geo--filtered" : "",
        `shopify-geo--layout-${layoutWidth}`,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showHeader ? (
        <DashboardSectionHeader
          className="shopify-geo__header"
          title={`${t("shopifyGeo.header.brand")} | ${t("shopifyGeo.header.title")}`}
          subtitle={dateRangeLabel}
          actionsClassName="dashboard-section-header__actions--filters"
          actions={
            <div className="dashboard-section-header__control-row">
              <div className="dashboard-section-header__control dashboard-section-header__control--wide">
                <OrganicDateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={todayISO}
                  onRangeChange={({ startDate: nextStartDate, endDate: nextEndDate }) => {
                    setStartDate(nextStartDate);
                    setEndDate(nextEndDate);
                  }}
                />
              </div>
              <DashboardExportButton
                dashboardName="Geo-Spatial"
                dateRange={{ startDate, endDate }}
                containerRef={pdfContainerRef}
                disabled={loading || Boolean(error)}
              />
            </div>
          }
        />
      ) : null}

      <ShopifyGeoActiveFilters
        showFilterBar={showFilterBar}
        filterChipText={filterChipText}
        filterRegionLabel={filterRegionLabel || null}
        resolvedCityDisplayName={resolvedCityDisplayName}
        heatmapFilterLabel={heatmapFilterLabel}
        removeRegionFilter={removeRegionFilter}
        removeCityFilter={removeCityFilter}
        removeTimeFilter={removeTimeFilter}
        onReset={() => setCrossFilter(null)}
      />

      <div ref={mainRef} className={`shopify-geo__main shopify-geo__main--${layoutWidth}`}>
        {showHeader ? (
          <div className="shopify-geo__narrative" data-pdf-block="narrative">
            <DashboardNarrativePanel
              narrative={narrative}
              loading={narrativePanelLoading}
              aiSummary={aiSummary}
            />
          </div>
        ) : null}
        {loading ? (
          <LoadingSpinner compact />
        ) : error ? (
          <div className="shopify-geo__error" role="status" aria-live="polite">
            {error}
          </div>
        ) : visibleRegions.length > 0 ? (
          <div
            className={[
              "shopify-geo__regions-scroll",
              visibleRegions.length === 1 ? "shopify-geo__regions-scroll--single" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div
              className={`shopify-geo__regions-grid ${
                visibleRegions.length === 1 ? "shopify-geo__regions-grid--single" : ""
              }`.trim()}
              data-pdf-block="geo-regions"
              data-pdf-splittable
              data-pdf-continue
            >
              {visibleRegions.map(region => (
                <ShopifyGeoRegionCard
                  key={inferPakistanRegionSlug(region.id, region.title)}
                  region={region}
                  crossFilter={crossFilter}
                  onCrossFilter={setCrossFilter}
                  weekDays={weekDays}
                  hourLabels={hourLabels}
                  mapSelectedCityNormalized={mapSelectedCityNormalized}
                  mapSelectedRegionId={mapSelectedRegionId}
                  isFocusedView={isFocusedFilterView}
                  heatmapFilterLabel={
                    activeRegionSlug &&
                    inferPakistanRegionSlug(region.id, region.title) === activeRegionSlug
                      ? heatmapFilterLabel
                      : null
                  }
                  filteredCityDisplayName={
                    activeRegionSlug &&
                    inferPakistanRegionSlug(region.id, region.title) === activeRegionSlug
                      ? resolvedCityDisplayName
                      : null
                  }
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ShopifyGeoSpatialDashboard;
